import {
  CourseLanguage,
  CourseraLesson,
  CourseraTask,
  OtherLesson,
  OtherLessonTask,
  Topic,
} from "@masterschool/course-builder-api"
import {
  useSelectedCourseraLesson,
  useSelectedOtherLesson,
} from "../../custom-hooks/useSelectedElementItem"
import { useAppDispatch, useAppSelector } from "@app/hooks"
import Droppable from "@cmp/droppable"
import HighlightableScrollRef from "@cmp/highlightableScrollRef"
import {
  useSensors,
  useSensor,
  PointerSensor,
  DndContext,
  DragOverlay,
} from "@dnd-kit/core"
import { restrictToVerticalAxis } from "@dnd-kit/modifiers"
import { Box, Typography } from "@mui/material"
import {
  forwardRef,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
} from "react"
import { createPortal } from "react-dom"
import {
  dragTaskStarted,
  dragTaskEnded,
  taskDraggedOver,
} from "@features/courseEditor/courseEditorSlice"
import OtherLessonTaskEditor from "./otherLessonTaskEditor"
import {
  useEditCourseraLesson,
  useEditOtherLesson,
} from "../../custom-hooks/useEditElementItem"
import CourseraTaskEditor from "./courseraTaskEditor"

type OtherLessonTasksEditorProps = {
  topic: Topic
  editingLanguage: CourseLanguage
}

const OtherLessonTasksEditor = forwardRef<
  HighlightableScrollRef,
  OtherLessonTasksEditorProps
>((props, ref) => {
  const { topic, editingLanguage } = props
  const otherLesson = useSelectedOtherLesson()
  const courseraLesson = useSelectedCourseraLesson()
  const lesson: OtherLesson | CourseraLesson | undefined =
    otherLesson ?? courseraLesson
  const tasksContainerRef = useRef<HTMLDivElement>(null)

  const [highlightLastTask, setHighlightLastTask] = useState(false)
  const scrollToBottomAndHighlightLastTask = useCallback(() => {
    if (!tasksContainerRef.current) {
      return
    }

    tasksContainerRef.current.onscrollend = () => {
      setHighlightLastTask(true)
      setTimeout(() => {
        setHighlightLastTask(false)
      }, 1000)

      if (tasksContainerRef.current) {
        tasksContainerRef.current.onscrollend = null
      }
    }
    tasksContainerRef.current.scrollTo({
      top: tasksContainerRef.current.scrollHeight,
      behavior: "smooth",
    })
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottomAndHighlightLastTask: scrollToBottomAndHighlightLastTask,
    }),
    [scrollToBottomAndHighlightLastTask],
  )

  if (!lesson) {
    return undefined
  }

  return (
    <Box
      ref={tasksContainerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        alignItems: "center",
        overflowY: "auto",
      }}
    >
      {courseraLesson && (
        <CourseraTasksList
          lesson={courseraLesson}
          highlightLastTask={highlightLastTask}
          topic={topic}
          editingLanguage={editingLanguage}
        />
      )}
      {otherLesson && (
        <OtherTasksList
          lesson={otherLesson}
          highlightLastTask={highlightLastTask}
          topic={topic}
          editingLanguage={editingLanguage}
        />
      )}
    </Box>
  )
})

function OtherTasksList(props: {
  lesson: OtherLesson
  highlightLastTask: boolean
  topic: Topic
  editingLanguage: CourseLanguage
}) {
  const { lesson, highlightLastTask, topic, editingLanguage } = props
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )
  const dispatch = useAppDispatch()
  const isDraggingTasksEnabled = useAppSelector(
    (state) => state.courseEditor.isDraggingTasksEnabled,
  )
  const draggedTaskId = useAppSelector(
    (state) => state.courseEditor.draggedTaskId,
  )
  const draggedTask = lesson.tasks.find((t) => t.id === draggedTaskId)
  const editLesson = useEditOtherLesson()
  const onChange = <K extends keyof OtherLessonTask>(
    task: OtherLessonTask,
    taskIndex: number,
    key: K,
    value: OtherLessonTask[K],
  ) => {
    editLesson(
      lesson,
      "tasks",
      [
        ...lesson.tasks.slice(0, taskIndex),
        {
          ...task,
          [key]: value,
        },
        ...lesson.tasks.slice(taskIndex + 1),
      ],
      topic,
    )
  }

  const [tasksBeingRemoved, setTasksBeingRemoved] = useState<string[]>([])

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      {lesson.tasks.length > 0 && (
        <Typography variant="body2_sb">Tasks</Typography>
      )}
      <DndContext
        onDragStart={(start) => {
          dispatch(dragTaskStarted({ id: start.active.id.toString() }))
        }}
        onDragEnd={() => {
          dispatch(dragTaskEnded())
        }}
        onDragCancel={() => {
          dispatch(dragTaskEnded())
        }}
        onDragOver={({ active, over }) => {
          if (!active || !over) {
            return
          }
          dispatch(
            taskDraggedOver({
              editStepId: window.crypto.randomUUID(),
              lessonId: lesson.id,
              sourceIndex: active.data.current?.sortable?.index,
              targetIndex: over.data.current?.sortable?.index,
            }),
          )
        }}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
      >
        <Droppable
          id={`${lesson.id}-tasks`}
          items={lesson.tasks.map((task, index) => {
            const isLast = index === lesson.tasks.length - 1
            return {
              element: (
                <OtherLessonTaskEditor
                  task={task}
                  index={index}
                  editingLanguage={editingLanguage}
                  onChange={(key, value) => {
                    onChange(task, index, key, value)
                  }}
                  onDeleteClicked={() => {
                    setTasksBeingRemoved([...tasksBeingRemoved, task.id])
                    setTimeout(() => {
                      editLesson(
                        lesson,
                        "tasks",
                        [
                          ...lesson.tasks.slice(0, index),
                          ...lesson.tasks.slice(index + 1),
                        ],
                        topic,
                      )
                    }, 200)
                  }}
                  sx={{
                    opacity:
                      tasksBeingRemoved.includes(task.id) ||
                      task.id === draggedTaskId
                        ? 0
                        : 1,
                    transform:
                      highlightLastTask && isLast
                        ? "scale(1.05)"
                        : task.id === draggedTaskId
                        ? "scale(0.95)"
                        : "scale(1)",
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              ),
              identifier: task.id,
            }
          })}
          placeholder={<></>}
          disabled={!isDraggingTasksEnabled}
        />
        {createPortal(
          <DragOverlay
            dropAnimation={{
              duration: 300,
              easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
            }}
            style={{
              zIndex: 9999999,
            }}
          >
            {draggedTask ? (
              <OtherLessonTaskEditor
                task={draggedTask}
                editingLanguage={editingLanguage}
                index={NaN}
                onChange={() => {}}
                onDeleteClicked={() => {}}
                sx={{
                  transform: "scale(0.95)",
                }}
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  )
}

function CourseraTasksList(props: {
  lesson: CourseraLesson
  highlightLastTask: boolean
  topic: Topic
  editingLanguage: CourseLanguage
}) {
  const { lesson, highlightLastTask, topic, editingLanguage } = props
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )
  const dispatch = useAppDispatch()
  const isDraggingTasksEnabled = useAppSelector(
    (state) => state.courseEditor.isDraggingTasksEnabled,
  )
  const draggedTaskId = useAppSelector(
    (state) => state.courseEditor.draggedTaskId,
  )
  const draggedTask = lesson.tasks.find((t) => t.id === draggedTaskId)
  const editLesson = useEditCourseraLesson()
  const onChange = <K extends keyof CourseraTask>(
    task: CourseraTask,
    taskIndex: number,
    key: K,
    value: CourseraTask[K],
  ) => {
    editLesson(
      lesson,
      "tasks",
      [
        ...lesson.tasks.slice(0, taskIndex),
        {
          ...task,
          [key]: value,
        },
        ...lesson.tasks.slice(taskIndex + 1),
      ],
      topic,
    )
  }

  const [tasksBeingRemoved, setTasksBeingRemoved] = useState<string[]>([])

  return (
    <div
      style={{
        width: "100%",
      }}
    >
      <DndContext
        onDragStart={(start) => {
          dispatch(dragTaskStarted({ id: start.active.id.toString() }))
        }}
        onDragEnd={() => {
          dispatch(dragTaskEnded())
        }}
        onDragCancel={() => {
          dispatch(dragTaskEnded())
        }}
        onDragOver={({ active, over }) => {
          if (!active || !over) {
            return
          }
          dispatch(
            taskDraggedOver({
              editStepId: window.crypto.randomUUID(),
              lessonId: lesson.id,
              sourceIndex: active.data.current?.sortable?.index,
              targetIndex: over.data.current?.sortable?.index,
            }),
          )
        }}
        sensors={sensors}
        modifiers={[restrictToVerticalAxis]}
      >
        <Droppable
          id={`${lesson.id}-tasks`}
          items={lesson.tasks.map((task, index) => {
            const isLast = index === lesson.tasks.length - 1
            return {
              element: (
                <CourseraTaskEditor
                  task={task}
                  index={index}
                  editingLanguage={editingLanguage}
                  onChange={(key, value) => {
                    onChange(task, index, key, value)
                  }}
                  onDeleteClicked={() => {
                    setTasksBeingRemoved([...tasksBeingRemoved, task.id])
                    setTimeout(() => {
                      editLesson(
                        lesson,
                        "tasks",
                        [
                          ...lesson.tasks.slice(0, index),
                          ...lesson.tasks.slice(index + 1),
                        ],
                        topic,
                      )
                    }, 200)
                  }}
                  sx={{
                    opacity:
                      tasksBeingRemoved.includes(task.id) ||
                      task.id === draggedTaskId
                        ? 0
                        : 1,
                    transform:
                      highlightLastTask && isLast
                        ? "scale(1.05)"
                        : task.id === draggedTaskId
                        ? "scale(0.95)"
                        : "scale(1)",
                    transition: "all 0.2s ease-in-out",
                  }}
                />
              ),
              identifier: task.id,
            }
          })}
          placeholder={<></>}
          disabled={!isDraggingTasksEnabled}
        />
        {createPortal(
          <DragOverlay
            dropAnimation={{
              duration: 300,
              easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
            }}
            style={{
              zIndex: 9999999,
            }}
          >
            {draggedTask ? (
              <CourseraTaskEditor
                task={draggedTask}
                editingLanguage={editingLanguage}
                index={NaN}
                onChange={() => {}}
                onDeleteClicked={() => {}}
                sx={{
                  transform: "scale(0.95)",
                }}
              />
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </div>
  )
}

export default OtherLessonTasksEditor
